import { useReducer, useCallback } from "react";
import SkipAnalyticsContext from "./context";
import SkipAnalyticsReducer, {
  initialState,
  LOADING,
  SET_OVERALL_STATS,
  SET_DRIVERS_STATS,
  SET_SKIP_HISTORY,
  SET_REASONS_BREAKDOWN,
  SET_DRIVERS_QUERY,
  SET_HISTORY_QUERY,
  RESET,
} from "./reducer";
import EndPoints from "../../services/end-points";
import { ISkipAnalyticsQuery, ISkipHistoryQuery } from "../../models/skip-analytics/query";
import { execute } from "../../utils/helpers/execute";

interface IProps {
  children: React.ReactNode;
}

const SkipAnalyticsContextProvider: React.FC<IProps> = ({ children }) => {
  const [state, dispatch] = useReducer(SkipAnalyticsReducer, initialState);

  const setLoading = (loading: string[]) => {
    dispatch({ type: LOADING, payload: loading });
  };

  const getOverallStatistics = useCallback(
    async (from_date?: string, to_date?: string) => {
      await execute({
        callback: async () => {
          setLoading([...state.loading, "overallStats"]);
          const response = await EndPoints.skipAnalytics.getOverallStatistics({
            from_date: from_date || state.driversQuery.from_date,
            to_date: to_date || state.driversQuery.to_date,
          });
          dispatch({ type: SET_OVERALL_STATS, payload: response.data });
        },
        fallback: (error) => {
          console.error("Failed to fetch overall statistics:", error);
        },
        finallyCallback: () => {
          setLoading(state.loading.filter((l) => l !== "overallStats"));
        },
        throwException: false,
      });
    },
    [state.loading, state.driversQuery]
  );

  const getDriversSkipStats = useCallback(async () => {
    await execute({
      callback: async () => {
        setLoading([...state.loading, "driversStats"]);
        const response = await EndPoints.skipAnalytics.getDriversSkipStats(
          state.driversQuery
        );
        dispatch({ type: SET_DRIVERS_STATS, payload: response.data });
      },
      fallback: (error) => {
        console.error("Failed to fetch drivers skip stats:", error);
      },
      finallyCallback: () => {
        setLoading(state.loading.filter((l) => l !== "driversStats"));
      },
      throwException: false,
    });
  }, [state.loading, state.driversQuery]);

  const getDriverSkipHistory = useCallback(
    async (driverId: number) => {
      await execute({
        callback: async () => {
          setLoading([...state.loading, "skipHistory"]);
          const response = await EndPoints.skipAnalytics.getDriverSkipHistory(
            driverId,
            state.historyQuery
          );
          dispatch({ type: SET_SKIP_HISTORY, payload: response.data });
        },
        fallback: (error) => {
          console.error("Failed to fetch driver skip history:", error);
        },
        finallyCallback: () => {
          setLoading(state.loading.filter((l) => l !== "skipHistory"));
        },
        throwException: false,
      });
    },
    [state.loading, state.historyQuery]
  );

  const getReasonsBreakdown = useCallback(
    async (driver_id?: number) => {
      await execute({
        callback: async () => {
          setLoading([...state.loading, "reasonsBreakdown"]);
          const response = await EndPoints.skipAnalytics.getReasonsBreakdown({
            from_date: state.driversQuery.from_date,
            to_date: state.driversQuery.to_date,
            driver_id,
          });
          dispatch({ type: SET_REASONS_BREAKDOWN, payload: response.data });
        },
        fallback: (error) => {
          console.error("Failed to fetch reasons breakdown:", error);
        },
        finallyCallback: () => {
          setLoading(state.loading.filter((l) => l !== "reasonsBreakdown"));
        },
        throwException: false,
      });
    },
    [state.loading, state.driversQuery]
  );

  const setDriversQuery = useCallback((query: ISkipAnalyticsQuery) => {
    dispatch({ type: SET_DRIVERS_QUERY, payload: query });
  }, []);

  const setHistoryQuery = useCallback((query: ISkipHistoryQuery) => {
    dispatch({ type: SET_HISTORY_QUERY, payload: query });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: RESET });
  }, []);

  return (
    <SkipAnalyticsContext.Provider
      value={{
        ...state,
        actions: {
          getOverallStatistics,
          getDriversSkipStats,
          getDriverSkipHistory,
          getReasonsBreakdown,
          setDriversQuery,
          setHistoryQuery,
          reset,
        },
      }}
    >
      {children}
    </SkipAnalyticsContext.Provider>
  );
};

export default SkipAnalyticsContextProvider;
